// Alternative Product Loader - Load product by SKU dynamically
if (!customElements.get('alternative-product-loader')) {
  customElements.define(
    'alternative-product-loader',
    class AlternativeProductLoader extends HTMLElement {
      constructor() {
        super();
        this.init();
      }

      async init() {
        const sku = this.dataset.sku;
        const customerType = this.dataset.customerType || 'private';
        const taxLabel = this.dataset.taxLabel || '(inkl. moms)';

        if (!sku) {
          this.showError('Ingen SKU angivet');
          return;
        }

        try {
          // Search for product by SKU
          const searchResponse = await fetch(`/search?q=${encodeURIComponent(sku)}&type=product`);
          
          if (!searchResponse.ok) {
            this.showError('Kunne ikke finde alternativt produkt');
            return;
          }

          const html = await searchResponse.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          
          // Find product card in search results
          const productCards = doc.querySelectorAll('.product-card');
          
          let foundProduct = null;
          
          for (const card of productCards) {
            const link = card.querySelector('a[href*="/products/"]');
            if (!link) continue;
            
            const href = link.getAttribute('href');
            const match = href.match(/\/products\/([^?#]+)/);
            if (!match || !match[1]) continue;
            
            const handle = match[1];
            
            // Fetch product data to verify SKU
            try {
              const prodResponse = await fetch(`/products/${handle}.js`);
              if (!prodResponse.ok) continue;
              
              const product = await prodResponse.json();
              
              // Check if any variant has matching SKU
              if (product.variants?.some(v => v.sku?.trim() === sku.trim())) {
                foundProduct = product;
                break;
              }
            } catch (e) {
              continue;
            }
          }

          if (foundProduct) {
            this.renderProduct(foundProduct, customerType, taxLabel);
          } else {
            this.showError('Alternativt produkt ikke fundet');
          }
        } catch (error) {
          console.error('Error loading alternative product:', error);
          this.showError('Kunne ikke indlæse alternativt produkt');
        }
      }

      renderProduct(product, customerType, taxLabel) {
        const contentDiv = this.querySelector('.alternative-product-section__content');
        if (!contentDiv) return;

        // Calculate price
        let price = product.price;
        if (customerType === 'business') {
          price = Math.round(price / 1.25);
        }

        const formattedPrice = this.formatMoney(price);
        const imageUrl = product.featured_image 
          ? this.getImageUrl(product.featured_image, 120)
          : '';

        const cardHTML = `
          <a href="${product.url}" class="alternative-product-card">
            ${imageUrl ? `
              <div class="alternative-product-card__image">
                <img
                  src="${imageUrl}"
                  alt="${this.escapeHtml(product.title)}"
                  width="120"
                  height="120"
                  loading="lazy"
                >
              </div>
            ` : ''}
            <div class="alternative-product-card__details">
              <h4 class="alternative-product-card__title h6">
                ${this.escapeHtml(product.title)}
              </h4>
              ${product.available ? `
                <div class="alternative-product-card__price">
                  ${formattedPrice}
                  <span class="alternative-product-card__tax">${taxLabel}</span>
                </div>
                <div class="alternative-product-card__cta">
                  <span class="btn btn--primary btn--small">
                    Se produkt
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 3L11 8L6 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </span>
                </div>
              ` : `
                <div class="alternative-product-card__unavailable text-subtext">
                  Ikke på lager
                </div>
              `}
            </div>
          </a>
        `;

        // Replace loading message with product card
        const loadingDiv = contentDiv.querySelector('.alternative-product-card__loading');
        if (loadingDiv) {
          loadingDiv.outerHTML = cardHTML;
        } else {
          contentDiv.insertAdjacentHTML('beforeend', cardHTML);
        }

        // Hide main product price and soldout badge
        this.hideMainProductElements();
      }

      hideMainProductElements() {
        // Find the product info container that contains this element
        const productInfoContainer = this.closest('.product__info-container');
        if (!productInfoContainer) return;

        // Find and hide the price block
        const priceBlock = productInfoContainer.querySelector('.product__blocks > .product__block--price');
        if (priceBlock) {
          priceBlock.style.display = 'none';
        }

        // Find and hide soldout badges (but not inside the alternative product card)
        const soldoutBadges = productInfoContainer.querySelectorAll('.f-badge--soldout');
        soldoutBadges.forEach(badge => {
          // Only hide if it's NOT inside the alternative product card
          if (!badge.closest('alternative-product-loader')) {
            badge.style.display = 'none';
          }
        });
      }

      showError(message) {
        const contentDiv = this.querySelector('.alternative-product-section__content');
        if (!contentDiv) return;

        const loadingDiv = contentDiv.querySelector('.alternative-product-card__loading');
        if (loadingDiv) {
          loadingDiv.innerHTML = `<p style="color: #c4301c;">${message}</p>`;
        }
      }

      formatMoney(cents) {
        const amount = (cents / 100).toFixed(2);
        return `${amount.replace('.', ',')} kr`;
      }

      getImageUrl(imageUrl, width) {
        if (!imageUrl) return '';
        
        // Handle Shopify CDN URLs
        if (imageUrl.includes('cdn.shopify.com')) {
          // Remove any existing size parameters
          const baseUrl = imageUrl.split('?')[0];
          // Add width parameter before file extension
          const parts = baseUrl.split('.');
          const ext = parts.pop();
          return `${parts.join('.')}_${width}x.${ext}`;
        }
        
        return imageUrl;
      }

      escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
      }
    }
  );
}
